import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { fetchFilteredArtistMixes, fetchFullMixTracklist } from '../../redux/thunk/djpalThunk';
import DJPalScrollableList from './DJPalScrollableList';
import MultiArtistText from '../MultiArtistText';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { entityId } from '../../utils/entityId';
import { useMixPlayback, canPlayMix } from './MixPlayer';
import PlayAllButton from '../common/PlayAllButton';
import { isWeb as _isWeb, notifyWebOnly } from '../../utils/webOnly';
import { useIsMobileWeb } from '../../hooks/useIsMobileWeb';
import MobileListSearch from '../common/MobileListSearch';
import CoverArt from '../CoverArt';

// Single-column mix list modal.
//
// Mirrors ArtistsListModal: a row per mix, each expandable to a list of
// tracks. Used for both "Mixes by them" (section='byArtist' — expansion
// is the mix's full tracklist) and "Mixes featuring them"
// (section='including' — expansion is the subject artist's tracks within
// the mix, from `tracksIncluded`).
const MixesListModal = ({
    title,
    subtitle,
    artistName,
    artistPublicId,
    section,
    dateRange,
    initialMixes = [],
    initialPagination = null,
    totalCount = null,
    onMixClick,
    onTrackClick,
    onArtistClick,
    onClose,
    baseFilters = null,
}) => {
    const dispatch = useDispatch();
    const [mixes, setMixes] = useState(initialMixes);
    const [pagination, setPagination] = useState(initialPagination);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filter, setFilter] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const [filterLoading, setFilterLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [tracksByMix, setTracksByMix] = useState({}); // id → { tracks, loading, error }
    const [loadError, setLoadError] = useState(null);
    const { playMix } = useMixPlayback();
    const didInitialFetchRef = useRef(false);
    useNativeMixCoverMode(); // re-render when the native cover flag flips

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Filter-effect: refetch on filter change. See TracksListModal for the
    // rationale on splitting initial-fetch into its own effect and
    // preserving loaded data on transient errors.
    useEffect(() => {
        const q = filter.trim();
        if (!q && !didInitialFetchRef.current) return undefined;
        if (baseFilters) return undefined; // one title filter per request: filter the loaded rows below
        let cancelled = false;
        setFilterLoading(true);
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistMixes({
                    section, artistName, artistId: artistPublicId, query: q || '', dateRange, page: 1, baseFilters,
                })).unwrap();
                if (cancelled) return;
                setMixes(result.mixes || []);
                setPagination(result.pagination || null);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Filter mixes failed:', err);
                setLoadError(err?.message || 'Failed to load mixes');
                if (q) {
                    setMixes([]);
                    setPagination(null);
                }
            } finally {
                if (!cancelled) setFilterLoading(false);
            }
        }, q ? 250 : 0);
        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, section, artistName, dateRange]);

    // One-shot initial fetch on mount.
    useEffect(() => {
        if (didInitialFetchRef.current) return undefined;
        didInitialFetchRef.current = true;
        let cancelled = false;
        setFilterLoading(true);
        (async () => {
            try {
                const result = await dispatch(fetchFilteredArtistMixes({
                    section, artistName, artistId: artistPublicId, query: '', dateRange, page: 1, baseFilters,
                })).unwrap();
                if (cancelled) return;
                setMixes(result.mixes || []);
                setPagination(result.pagination || null);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Initial mixes fetch failed:', err);
                setLoadError(err?.message || 'Failed to load mixes');
            } finally {
                if (!cancelled) setFilterLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !pagination?.hasNext) return;
        setLoadingMore(true);
        setLoadError(null);
        try {
            const result = await dispatch(fetchFilteredArtistMixes({
                section, artistName, artistId: artistPublicId, query: filter.trim(), dateRange,
                page: (pagination.page || 1) + 1, baseFilters,
            })).unwrap();
            setMixes(prev => [...prev, ...(result.mixes || [])]);
            if (result.pagination) setPagination(result.pagination);
        } catch (err) {
            console.error('Load more mixes failed:', err);
            setLoadError(err?.message || 'Failed to load more mixes');
        } finally {
            setLoadingMore(false);
        }
    }, [dispatch, loadingMore, pagination, section, artistName, artistPublicId, filter, dateRange]);

    // Pick the right track list for the expansion based on the section.
    //   - byArtist  → full tracklist (mix.tracklist)
    //   - including → subject artist's tracks in the mix (mix.tracksIncluded)
    const getTracksForMix = useCallback((mix) => {
        const raw = section === 'including'
            ? (mix.tracksIncluded || mix.tracklist || [])
            : (mix.tracklist || mix.tracksIncluded || []);
        // Tracklist entries may be { track: {...}, order } or the track itself.
        return raw.map(entry => entry?.track ? entry.track : entry).filter(Boolean);
    }, [section]);

    const handleToggleExpand = useCallback(async (mix) => {
        const key = entityId(mix) || mix.link || mix.title;
        if (expandedId === key) { setExpandedId(null); return; }
        setExpandedId(key);
        const local = getTracksForMix(mix);
        if (local.length > 0 || section === 'including') {
            // 'including' mode trusts `tracksIncluded` as the authoritative
            // subject-filtered list — no fetch needed.
            setTracksByMix(prev => ({ ...prev, [key]: { tracks: local, loading: false } }));
            return;
        }
        // Byartist mode with no local tracklist: fetch it.
        if (tracksByMix[key]?.tracks) return;
        setTracksByMix(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
        try {
            await dispatch(fetchFullMixTracklist({
                mixId: mix.id,
                publicId: entityId(mix),
            }));
            // fetchFullMixTracklist dispatches an update to the store, not
            // a return value. Refetch via a one-shot searchMixes call.
            // Simpler: re-derive from the updated mix object if the parent
            // refreshes — but inside this modal we hold a snapshot.
            // Fallback: hit searchMixes inline to get the tracklist.
            const token = localStorage.getItem('djpal:token');
            const result = token
                ? await window.electron.djpal.searchMixes(token, { public_id: entityId(mix) })
                : null;
            const data = result?.data || result;
            const arr = data?.results || data?.result || (Array.isArray(data) ? data : []);
            const fresh = Array.isArray(arr) ? arr[0] : null;
            const tracks = fresh ? getTracksForMix(fresh) : [];
            setTracksByMix(prev => ({ ...prev, [key]: { tracks, loading: false } }));
        } catch (err) {
            setTracksByMix(prev => ({
                ...prev,
                [key]: { tracks: [], loading: false, error: err?.message || 'Failed to load' },
            }));
        }
    }, [expandedId, getTracksForMix, tracksByMix, section, dispatch]);

    // Prefer the paginated endpoint's own totalCount over the parent prop —
    // the parent stat is computed differently and can disagree with what the
    // modal is actually fetching (e.g. after the displayability filter
    // excludes placeholder-only mixes).
    const total = pagination?.totalCount ?? totalCount ?? mixes.length;
    const shownMixes = (baseFilters && filter.trim())
        ? mixes.filter((m) => `${m.title || ''} ${m.artist || m.djName || ''} ${(m.artists || []).map(a => a.name).join(' ')}`.toLowerCase().includes(filter.trim().toLowerCase()))
        : mixes;
    const isFiltering = !!filter.trim();

    return ReactDOM.createPortal(
        <div className="djpal-modal-backdrop" onMouseDown={onClose}>
            <div className="djpal-modal djpal-artists-list-modal djpal-mixes-list-modal" onMouseDown={e => e.stopPropagation()}>
                <div className="djpal-modal-header">
                    <div className="djpal-modal-title-group">
                        <h3 className="djpal-modal-title">{title}</h3>
                        {subtitle && <div className="djpal-modal-subtitle">{subtitle}</div>}
                    </div>
                    {mixes.some(m => canPlayMix(m)) && (
                        <PlayAllButton
                            from="mixes_modal"
                            identityItems={mixes}
                            onStart={async () => {
                                if (_isWeb) { notifyWebOnly('mix'); return false; }
                                const idx = mixes.findIndex(m => canPlayMix(m));
                                if (idx < 0) return false;
                                await playMix(mixes[idx], { items: mixes, index: idx });
                                return true;
                            }}
                        />
                    )}
                    <button className="djpal-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="djpal-modal-body">
                    {isMobileWeb ? (
                        <div className="djpal-artists-list-modal-search djpal-artists-list-modal-search--mobile">
                            <MobileListSearch value={filter} onChange={setFilter} placeholder="Filter mixes" ariaLabel="Filter mixes" />
                        </div>
                    ) : (
                        <div className="djpal-artists-list-modal-search">
                            <input
                                type="text"
                                placeholder="Filter mixes…"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                aria-label="Filter mixes"
                            />
                            {filter && (
                                <button type="button" className="djpal-artists-list-modal-search-clear" onClick={() => setFilter('')} aria-label="Clear filter">×</button>
                            )}
                        </div>
                    )}
                    {filterLoading && mixes.length === 0 ? (
                        <div className="djpal-modal-skel" aria-label="Loading">
                            {[...Array(8).keys()].map((i) => (
                                <div key={i} className="djpal-modal-skel-row">
                                    <span className="djpal-modal-skel-art" />
                                    <span className="djpal-modal-skel-text">
                                        <span className="djpal-modal-skel-line" />
                                        <span className="djpal-modal-skel-line short" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : shownMixes.length === 0 ? (
                        <div className="djpal-empty-hint">
                            {loadError ? `Couldn't load mixes: ${loadError}` : (isFiltering ? `No matches for "${filter.trim()}"` : 'No mixes found.')}
                        </div>
                    ) : (
                        <DJPalScrollableList
                            maxHeight="55vh"
                            onScrollBottom={pagination?.hasNext ? handleLoadMore : undefined}
                        >
                            {shownMixes.map((m, i) => {
                                const key = entityId(m) || m.link || `${m.title}-${i}`;
                                const expanded = expandedId === key;
                                const detail = tracksByMix[key];
                                const art = resolveMixCover(m) || '';
                                const dj = m.djName || m.artist || '';
                                const hasDj = (m.artists?.length > 0) || !!dj;
                                const titleText = m.title || m.eventName || '(untitled mix)';
                                const tailParts = [];
                                if (m.date) tailParts.push(m.date);
                                if (section === 'including' && (m.tracksIncluded?.length || m.tracksCount)) {
                                    const n = m.tracksCount ?? m.tracksIncluded.length;
                                    tailParts.push(`${n} ${n === 1 ? 'track' : 'tracks'}`);
                                }
                                const tail = tailParts.join(' · ');
                                const sub = [dj, ...tailParts].filter(Boolean).join(' · ');
                                return (
                                    <div key={key} className={`djpal-artists-list-modal-row${expanded ? ' is-expanded' : ''}`}>
                                        <div
                                            className="djpal-artists-list-modal-head clickable"
                                            onClick={() => handleToggleExpand(m)}
                                        >
                                            {art ? (
                                                <CoverArt className="artist-avatar" src={art} size={24} />
                                            ) : (
                                                <div className="artist-avatar">&#9835;</div>
                                            )}
                                            <div className="djpal-artists-list-modal-meta">
                                                <span className="djpal-artists-list-modal-name" title={titleText}>{titleText}</span>
                                                {(hasDj || tail) && (
                                                    <span className="djpal-artists-list-modal-sub" title={sub}>
                                                        <MultiArtistText
                                                            artists={m.artists}
                                                            fallbackText={dj}
                                                            segmentClassName={onArtistClick ? 'clickable-artist' : undefined}
                                                            onSelect={onArtistClick ? (a) => { onArtistClick(a); onClose(); } : undefined}
                                                        />
                                                        {hasDj && tail ? ' · ' : ''}
                                                        {tail}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="djpal-artists-list-modal-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
                                        </div>
                                        {expanded && (
                                            <div className="djpal-artists-list-modal-tracks">
                                                {detail?.loading && (
                                                    <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                                )}
                                                {detail?.error && (
                                                    <div className="djpal-empty-hint">Couldn't load tracks: {detail.error}</div>
                                                )}
                                                {!detail?.loading && !detail?.error && detail?.tracks?.length === 0 && (
                                                    <div className="djpal-empty-hint">No tracks found.</div>
                                                )}
                                                {detail?.tracks?.map((t, ti) => {
                                                    const tArt = t.artworkUrl || t.artwork || t.image || '';
                                                    const label = t.artist ? `${t.artist} – ${t.title}` : t.title;
                                                    return (
                                                        <div
                                                            key={`${key}-t-${ti}`}
                                                            className="djpal-artists-list-modal-track clickable"
                                                            onClick={(e) => { e.stopPropagation(); onTrackClick?.(t); onClose(); }}
                                                            title={label}
                                                        >
                                                            {tArt ? (
                                                                <CoverArt className="track-artwork-mini" src={tArt} size={28} />
                                                            ) : (
                                                                <div className="track-artwork-mini placeholder">&#9835;</div>
                                                            )}
                                                            <span className="track-label">{label}</span>
                                                        </div>
                                                    );
                                                })}
                                                {detail?.tracks?.length > 0 && onMixClick && (
                                                    <div
                                                        className="djpal-artists-list-modal-track clickable djpal-artists-list-modal-track-link"
                                                        onClick={(e) => { e.stopPropagation(); onMixClick(m); onClose(); }}
                                                    >
                                                        <span className="track-label">Open mix →</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {loadingMore && (
                                <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                            )}
                            {!loadingMore && loadError && mixes.length > 0 && (
                                <div className="djpal-empty-hint">Couldn't load more: {loadError}</div>
                            )}
                        </DJPalScrollableList>
                    )}
                </div>
                <div className="djpal-modal-footer">
                    {isFiltering
                        ? `${mixes.length}${pagination?.totalCount != null ? ` of ${pagination.totalCount}` : ''} matching`
                        : `${mixes.length}${total != null && total !== mixes.length ? ` of ${total}` : ''} ${total === 1 ? 'mix' : 'mixes'}`}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default MixesListModal;
